import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/apply", label: "Free Website" },
  { to: "/services", label: "Services" },
  { to: "/packages", label: "Packages" },
  { to: "/hosting", label: "Hosting" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#05050a]/75 border-b border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
          : "backdrop-blur-md bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-2.5 group"
        >
          <div className="w-10 h-10 grid place-items-center transition-transform duration-300 group-hover:scale-105">
            <img src="/equinox-logo.png" alt="Equinox Systems logo" className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(10,132,255,0.55)]" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold tracking-tight text-white">
              Equinox Systems
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">
              Freedom Core Ventures
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm rounded-full transition-all duration-200 ${
                  isActive
                    ? "text-white bg-white/[0.07] border border-white/10"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/quote" className="btn-ghost text-sm !py-2.5 !px-4" data-testid="nav-get-quote-btn">
            Get Quote
          </Link>
          <Link to="/apply" className="btn-primary text-sm !py-2.5 !px-4" data-testid="nav-apply-btn">
            Apply Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 rounded-lg border border-white/10 text-white bg-white/[0.03]"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#05050a]/95 backdrop-blur-2xl">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-gray-300 hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <Link to="/quote" className="btn-ghost w-full" data-testid="mobile-get-quote-btn">
                Get Custom Quote
              </Link>
              <Link to="/apply" className="btn-primary w-full" data-testid="mobile-apply-btn">
                Apply For A Free Website
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
